'use client';

import type { FC } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Progress,
  Radio,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { usePermission } from '~/app/auth/hooks/usePermission';
import {
  KMS_ACCEPT,
  KMS_MAX_FILE_MB,
} from '~/app/services/kms/kms-document.utils';
import { KmsDocumentStatusBadge } from '~/app/components/kms/KmsDocumentStatusBadge/KmsDocumentStatusBadge';
import { useKmsUploadPanel } from './hooks/useKmsUploadPanel';

export interface KmsUploadPanelProps {
  organizationId: string | null;
  isSuperAdmin?: boolean;
  isPlatformLibrary?: boolean;
  presetAssetId?: string;
  /** When true, hides the outer card chrome (page already provides layout). */
  embedded?: boolean;
}

export const KmsUploadPanel: FC<KmsUploadPanelProps> = ({
  organizationId,
  isSuperAdmin = false,
  isPlatformLibrary = false,
  presetAssetId,
  embedded = false,
}) => {
  const { t } = useTranslation('kms');
  const { hasPermission } = usePermission();
  const canUpload = hasPermission(PermissionKey.DOCUMENTS_UPLOAD);
  const panel = useKmsUploadPanel({
    organizationId,
    isSuperAdmin,
    isPlatformLibrary,
    presetAssetId,
  });

  const form = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {!embedded ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {isPlatformLibrary
            ? t('upload.platformHint', { maxSize: KMS_MAX_FILE_MB })
            : t('upload.hint', { maxSize: KMS_MAX_FILE_MB })}
        </Typography.Paragraph>
      ) : (
        <Typography.Paragraph type="secondary">
          {isPlatformLibrary
            ? t('upload.platformHint', { maxSize: KMS_MAX_FILE_MB })
            : t('upload.hint', { maxSize: KMS_MAX_FILE_MB })}
        </Typography.Paragraph>
      )}

      {!canUpload ? (
        <Alert type="info" showIcon message={t('upload.readOnly')} />
      ) : null}

      <Form layout="vertical">
        <Form.Item
          label={t('upload.fields.files')}
          extra={t('upload.batchHint')}
        >
          <Upload.Dragger
            multiple
            accept={KMS_ACCEPT}
            fileList={panel.uploadFileList}
            beforeUpload={panel.beforeUpload}
            onChange={panel.onUploadChange}
            showUploadList={{ showRemoveIcon: !panel.isUploading }}
            disabled={panel.isUploading || !canUpload}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('upload.dragText')}</p>
            <p className="ant-upload-hint">{t('upload.dragHint')}</p>
          </Upload.Dragger>
          {panel.selectedFiles.length > 0 ? (
            <Flex
              justify="space-between"
              align="center"
              style={{ marginTop: 8 }}
            >
              <Typography.Text type="secondary">
                {t('upload.selectedCount', {
                  count: panel.selectedFiles.length,
                })}
              </Typography.Text>
              <Button
                type="link"
                size="small"
                disabled={panel.isUploading}
                onClick={panel.clearSelectedFiles}
              >
                {t('upload.clearSelection')}
              </Button>
            </Flex>
          ) : null}
        </Form.Item>

        <Form.Item label={t('upload.fields.documentType')}>
          <Select
            allowClear
            disabled={!canUpload || panel.isUploading}
            placeholder={t('upload.fields.documentTypePlaceholder')}
            options={panel.documentTypeOptions}
            value={panel.documentType}
            onChange={(value) => panel.setDocumentType(value)}
          />
        </Form.Item>

        {!isPlatformLibrary ? (
          <>
            <Form.Item label={t('upload.fields.scope')}>
              <Radio.Group
                disabled={!canUpload || panel.isUploading}
                value={panel.scopeKind}
                onChange={(event) =>
                  panel.onScopeKindChange(event.target.value)
                }
              >
                <Radio.Button value="global">
                  {t('upload.scope.orgWide')}
                </Radio.Button>
                <Radio.Button value="site">
                  {t('upload.scope.site')}
                </Radio.Button>
                <Radio.Button value="asset">
                  {t('upload.scope.asset')}
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            {panel.scopeKind === 'site' ? (
              <Form.Item label={t('upload.fields.site')} required>
                <Select
                  showSearch
                  optionFilterProp="label"
                  disabled={!canUpload || panel.isUploading}
                  loading={panel.sitesLoading}
                  placeholder={t('upload.fields.sitePlaceholder')}
                  options={panel.siteOptions}
                  value={panel.siteId}
                  onChange={(value) => panel.setSiteId(value)}
                />
              </Form.Item>
            ) : null}

            {panel.scopeKind !== 'site' ? (
              <Form.Item
                label={t('upload.fields.asset')}
                required={panel.scopeKind === 'asset'}
              >
                <Select
                  allowClear={panel.scopeKind !== 'asset'}
                  showSearch
                  optionFilterProp="label"
                  disabled={!canUpload || panel.isUploading}
                  loading={panel.assetsLoading}
                  placeholder={t('upload.fields.assetPlaceholder')}
                  options={panel.assetOptions}
                  value={panel.assetId}
                  onChange={(value) => panel.onAssetChange(value)}
                />
              </Form.Item>
            ) : null}
          </>
        ) : (
          <Alert
            type="info"
            showIcon
            message={t('upload.scope.platform')}
            style={{ marginBottom: 16 }}
          />
        )}

        {panel.batchProgress ? (
          <div style={{ marginBottom: 16 }}>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 8 }}
            >
              {t('upload.batchProgress', {
                current: panel.batchProgress.current,
                total: panel.batchProgress.total,
              })}
            </Typography.Text>
            <Progress
              percent={Math.round(
                (panel.batchProgress.current / panel.batchProgress.total) * 100,
              )}
              status="active"
              showInfo={false}
            />
          </div>
        ) : null}

        <Button
          type="primary"
          onClick={() => void panel.submitUpload()}
          loading={panel.isUploading}
          disabled={panel.selectedFiles.length === 0 || !canUpload}
        >
          {panel.submitLabel}
        </Button>
      </Form>

      <Card size="small" title={t('upload.recentTitle')}>
        {panel.trackedUploads.length === 0 ? (
          <Empty description={t('upload.emptyRecent')} />
        ) : (
          <Flex vertical gap={12}>
            {panel.trackedUploads.map((item) => (
              <Flex
                key={item.localId}
                justify="space-between"
                align="center"
                gap={16}
              >
                <Typography.Text ellipsis style={{ maxWidth: '50%' }}>
                  {item.fileName}
                </Typography.Text>
                <KmsDocumentStatusBadge
                  documentId={item.documentId}
                  fileName={item.fileName}
                  organizationId={organizationId}
                  isSuperAdmin={isSuperAdmin}
                />
              </Flex>
            ))}
          </Flex>
        )}
      </Card>
    </Space>
  );

  if (embedded) {
    return form;
  }

  return <Card title={t('upload.title')}>{form}</Card>;
};
